<template>
  <li class="k-sidebar__item k-sidebar__item--group">
    <!-- Parent Link (klickbar zum Auf-/Zuklappen) -->
    <div
      class="k-sidebar__link k-sidebar__link--parent"
      :class="{ 
        'k-sidebar__link--expanded': isExpanded,
        'k-sidebar__link--active': isActive
      }"
      @click="toggleExpanded"
    >
      <span class="k-sidebar__link-icon">
        <i v-if="icon && !useShieldIcon && !useEuroIcon" :class="icon"></i>
        <svg
          v-if="useShieldIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <svg
          v-if="useEuroIcon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#84A98C"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M17 5.5a6 6 0 0 0-9.5 2.1A6 6 0 0 0 7 16.5a6 6 0 0 0 9 0" />
          <path d="M7 10h7" />
          <path d="M7 14h6.5" />
        </svg>
      </span>
      <span class="k-sidebar__link-text">{{ name }}</span>
      <span class="k-sidebar__link-arrow">
        <i :class="isExpanded ? 'tim-icons icon-minimal-down' : 'tim-icons icon-minimal-right'"></i>
      </span>
    </div>
    
    <!-- Children (werden eingeklappt/ausgeklappt) -->
    <transition name="slide">
      <ul v-if="isExpanded" class="k-sidebar__submenu">
        <sidebar-link
          v-for="child in children"
          :key="child.path"
          :to="child.path"
          :name="child.name"
          :icon="child.icon"
          :use-shield-icon="child.useShieldIcon || false"
          :use-euro-icon="child.useEuroIcon || false"
        />
      </ul>
    </transition>
  </li>
</template>

<script>
import SidebarLink from "./SidebarLink.vue";

/**
 * CollapsibleSidebarLink - Verschachteltes Sidebar-Menü
 * @description Sidebar-Link mit aufklappbaren Unterseiten (Accordion)
 * @props
 * - name {String} - Name des Parent-Links
 * - icon {String} - Icon-Klasse
 * - useShieldIcon {Boolean} - Shield SVG Icon verwenden
 * - useEuroIcon {Boolean} - Euro SVG Icon verwenden
 * - children {Array} - Array von Child-Links
 * - path {String} - Optional: Path für Parent-Link
 */
export default {
  name: "CollapsibleSidebarLink",
  components: {
    SidebarLink,
  },
  props: {
    name: { type: String, required: true },
    icon: { type: String, default: "" },
    useShieldIcon: { type: Boolean, default: false },
    useEuroIcon: { type: Boolean, default: false },
    children: { type: Array, default: () => [] },
    path: { type: String, default: "" },
  },
  data() {
    return {
      isExpanded: false,
    };
  },
  computed: {
    /**
     * Prüft ob einer der Child-Links aktiv ist
     * @returns {Boolean}
     */
    isActive() {
      if (!this.children || this.children.length === 0) return false;
      return this.children.some(child => {
        const route = this.$route;
        return route.path === child.path || route.path.startsWith(child.path + '/');
      });
    },
  },
  watch: {
    /**
     * Öffnet automatisch wenn ein Child-Link aktiv ist
     */
    isActive(newVal) {
      if (newVal) {
        this.isExpanded = true;
      }
    },
  },
  mounted() {
    // Öffne automatisch wenn ein Child-Link aktiv ist
    if (this.isActive) {
      this.isExpanded = true;
    }
  },
  methods: {
    /**
     * Klappt das Menü auf/zu
     */
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
      // Optional: Wenn Parent auch ein Link ist, navigiere dorthin
      if (this.path && !this.isExpanded) {
        this.$router.push(this.path);
      }
    },
  },
};
</script>

<style scoped>
.k-sidebar__item--group {
  position: relative;
}

.k-sidebar__link--parent {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  position: relative;
}

.k-sidebar__link-arrow {
  margin-left: auto;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.k-sidebar__link--expanded .k-sidebar__link-arrow {
  transform: rotate(90deg);
}

.k-sidebar__submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  padding-left: 20px; /* Einrückung für Unterpunkte */
  background: rgba(0, 0, 0, 0.1);
  border-left: 2px solid rgba(255, 255, 255, 0.1);
  margin-left: 8px;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
